/* eslint-disable complexity */
import type {
  ButtonShape,
  DropdownPlacement,
  GroupedOptions,
  OptionType,
  Options,
  Placement
} from '@eggplant/types'
import type {
  ControllerStateAndHelpers,
  DownshiftState,
  GetItemPropsOptions,
  GetItemPropsReturnValue,
  GetRootPropsReturnValue,
  Overwrite,
  StateChangeOptions
} from 'downshift'
import Downshift from 'downshift'
import natsort from 'natsort'
import type {
  CSSProperties,
  ReactNode,
  UIEventHandler
} from 'react'
import {
  useEffect,
  useRef,
  useState
} from 'react'
import { HiCheck } from 'react-icons/hi'
import { Tooltip } from '../..'
import Divider from '../Divider/Divider'
import SearchInput from '../SearchInput/SearchInput'
import ClearButton from './ClearButton'
import classes from './Combobox.module.scss'
import EmptyState from './EmptyState/EmptyState'
import {
  maximumGroupListDescription,
  maximumListDescription
} from './MaximumListDescription'
import { FiAlertCircle } from 'react-icons/fi'

export interface Props {
  customValidity?: string
  groupedOptions?: GroupedOptions
  initialGroupedOptions?: GroupedOptions
  options?: Options
  initialOptions?: Options
  isRequired?: boolean
  hasSearch?: boolean
  hasClearButton?: boolean
  onChange: (value: string) => void
  renderButton: (props: ButtonShape) => ReactNode
  value?: string
  isDisabled?: boolean
  noOptionsPlaceholder?: ReactNode
  isFilter?: boolean
  isFilterFixedMinMaxWidth?: boolean
  externalQuery?: ReactNode
  onClear?: () => void
  isCustomBoxShadow?: boolean
  optionsLimit?: number
  placement?: Placement
  dropdownPlacement?: DropdownPlacement
  footer?: ReactNode
  isInvalid?: boolean
}

const MAXIMUM_OPTIONS_COUNT = 500

const itemToString = (option?: OptionType | null) => option
  ? option.value
  : ''

const limitAndGroupOptions = (groupedOptions: GroupedOptions, limit: number): GroupedOptions => groupedOptions.reduce<[GroupedOptions, number]>(
  ([acc, remainingLimit], group) => {
    if (remainingLimit <= 0) return [acc, remainingLimit]

    const optionsToTake = group.options.slice(0, remainingLimit)
    const updatedLimit = remainingLimit - optionsToTake.length

    return [
      [
        ...acc,
        ...optionsToTake.length > 0
          ? [{
            ...group,
            options: optionsToTake
          }]
          : []
      ],
      updatedLimit
    ]
  },
  [[], limit]
)[0]

const getTopPosition = (offset: number, options: Options) => {
  if (options.length === 0) return '-2px'

  const iter = options.length >= 10
    ? 10
    : options.length

  const topPos = offset > 320
    ? 420 - (30 * iter)
    : -2

  return `${topPos}px`
}

const Combobox = ({
  customValidity = 'Please select an item in the list.',
  groupedOptions = [],
  options = [],
  onChange,
  renderButton,
  initialOptions = [],
  initialGroupedOptions = [],
  isRequired = false,
  value = '',
  isDisabled = false,
  noOptionsPlaceholder,
  hasSearch = true,
  hasClearButton = true,
  isFilter = false,
  isFilterFixedMinMaxWidth = true,
  externalQuery,
  onClear,
  placement,
  dropdownPlacement = 'bottom',
  optionsLimit = 0,
  footer,
  isInvalid = false
}: Props) => {
  // This help controls the InputText's input
  const [input, setInput] = useState('')
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const [windowHeight, setWindowHeight] = useState(window.screen.height)
  const inputRef = useRef<HTMLInputElement>(null)
  const comboBoxRef = useRef<HTMLDivElement>(null)
  const divRef = useRef<HTMLDivElement>(null)
  const [defaultOptionCount, setDefaultOptionCount] = useState(optionsLimit)

  const sorter = natsort({ insensitive: true })

  // eslint-disable-next-line fp/no-mutating-methods
  const sortedOptions = options.sort((current, next) =>
    sorter(current.label, next.label)
  )

  // eslint-disable-next-line fp/no-mutating-methods
  const sortedGroupOptions = groupedOptions.sort((current, next) =>
    sorter(current.label, next.label)
  )

  const searchedOptions = input
    ? sortedOptions.filter(option =>
      option.label.toLowerCase().includes(input.toLowerCase())
    )
    : sortedOptions

  const searchedGroupedOptions = input
    ? sortedGroupOptions.map(group => (
      {
        ...group,
        options: group.options.filter(option =>
          option.label.toLowerCase().includes(input.toLowerCase()))
      }
    ))
    : sortedGroupOptions

  const filteredOptions = defaultOptionCount
    ? searchedOptions.slice(0, defaultOptionCount)
    : searchedOptions

  const filteredGroupedOptions = defaultOptionCount
    ? limitAndGroupOptions(searchedGroupedOptions, defaultOptionCount)
    : searchedGroupedOptions

  const hasInitialOptions = !!filteredOptions.length || !!filteredGroupedOptions.map(group => group.options).flat().length

  const handleResize = () => {
    setWindowWidth(window.innerWidth)
    setWindowHeight(window.screen.height)
  }

  window.addEventListener('resize', handleResize)

  const onInputValueChange = (inputValue: string | null, stateAndHelpers: ControllerStateAndHelpers<OptionType>) => {
    const optionsNumber = (stateAndHelpers.selectedItem?.value === inputValue) || inputValue === ''
      ? defaultOptionCount || optionsLimit
      : optionsLimit

    optionsLimit && setDefaultOptionCount(optionsNumber)
    setInput(inputValue || '')
  }

  const onOuterClick = () => {
    optionsLimit && setDefaultOptionCount(optionsLimit)
    setInput('')
  }

  const handleSelection = (option: OptionType | null) => {
    if (!option || option.value === value) return

    onChange(option.value)

    setInput('')
  }

  // AUB-2625
  const stateReducer = (state: DownshiftState<OptionType>, changes: StateChangeOptions<OptionType>) => {
    if (changes.type === Downshift.stateChangeTypes.keyDownEscape) {
      return {
        ...changes,
        selectedItem: state.selectedItem,
        highlightedIndex: null,
        isOpen: false,
        inputValue: ''
      }
    }

    // AUB-17991
    return {
      ...changes,
      inputValue: ''
    }
  }

  const renderOptionLabel = (item: OptionType) =>
    <div className={classes.checkboxStyle}>
      <div className={classes.textStyle}>
        {item.label}
      </div>
      {
        item.value === value &&
          <HiCheck
            aria-hidden
            aria-label='Combobox selected checked'
            className={classes.CheckIcon}
            size={16}
          />
      }
    </div>

  const renderInitialOptions = (getItemProps: <Options> (initialOptions: GetItemPropsOptions<OptionType> & Options) => Omit<Overwrite<GetItemPropsReturnValue, Options>, 'index' | 'item'>) => initialOptions.length
    ? (
      <>
        {
          initialOptions.map(item =>
            <li
              key={item.value}
              {...getItemProps({ item })}
            >
              <Tooltip label={item.label}>
                {renderOptionLabel(item)}
              </Tooltip>
            </li>
          )
        }
        <Divider />
      </>
    )
    : null

  const renderOptions = (getItemProps: <Options> (filteredOptions: GetItemPropsOptions<OptionType> & Options) => Omit<Overwrite<GetItemPropsReturnValue, Options>, 'index' | 'item'>) => {
    const renderOptionsList = filteredOptions.length
      ? filteredOptions.map(item =>
        <li
          key={item.value}
          {...getItemProps({ item })}
        >
          <Tooltip label={item.label}>
            {renderOptionLabel(item)}
          </Tooltip>
        </li>
      )
      : null

    return filteredOptions.length
      ? renderOptionsList
      : <EmptyState />
  }

  const renderInitialGroupedOptions = (getItemProps: ({ item }: any) => Omit<Overwrite<GetItemPropsReturnValue, Options>, 'index' | 'item'>) => initialGroupedOptions.length
    ? initialGroupedOptions.map(group =>
      <>
        <strong className={classes.GroupStyle}>
          {group.label}
        </strong>
        {
          group.options.map(item =>
            <li
              key={item.value}
              {...getItemProps({ item })}
              className={classes.GroupStyle}
            >
              <Tooltip label={item.label}>
                {renderOptionLabel(item)}
              </Tooltip>
            </li>
          )
        }
      </>
    )
    : null

  const renderGroupedOptions = (getItemProps: ({ item }: any) => Omit<Overwrite<GetItemPropsReturnValue, Options>, 'index' | 'item'>) => {
    const availableOptions = filteredGroupedOptions.map(group => group.options).flat()

    return availableOptions.length
      ? filteredGroupedOptions.map(group =>
        <>
          <strong className={classes.GroupStyle}>
            {group.label}
          </strong>
          {
            group.options.map(item =>
              <li
                key={item.value}
                {...getItemProps({ item })}
                className={classes.GroupStyle}
              >
                <Tooltip label={item.label}>
                  {renderOptionLabel(item)}
                </Tooltip>
              </li>
            )
          }
        </>
      )
      : <EmptyState />
  }

  const validate = () => {
    const ref = inputRef.current

    if (!ref) return

    // reset CustomValidity when value changed
    ref.setCustomValidity('')
  }

  const handleClear = () => {
    onChange('')

    onClear && onClear()
  }

  const handleScroll: UIEventHandler<HTMLUListElement> | undefined = event => {
    if (!optionsLimit) return

    const {
      offsetHeight,
      scrollHeight,
      scrollTop
    } = event.target as HTMLDivElement

    // TODO: Allow a tolerance before hitting the bottom
    const isAtBottom = scrollTop + offsetHeight >= scrollHeight

    if (isAtBottom && defaultOptionCount) {
      if (defaultOptionCount < MAXIMUM_OPTIONS_COUNT) setDefaultOptionCount(defaultOptionCount + optionsLimit)
    }
  }

  const clearSectionClassName = externalQuery
    ? classes.clearSection + ' ' + classes.justifySpaceBetween
    : classes.clearSection

  const renderClearSection =
    <div className={clearSectionClassName}>
      {externalQuery && externalQuery}
      {value && <ClearButton onClear={handleClear} />}
    </div>

  useEffect(validate, [value])

  const getComboBoxClasses = () => {
    const enabledClass = !isDisabled
      ? classes.Enabled
      : ''

    const hasControlledWidthClass = isFilter && isFilterFixedMinMaxWidth
      ? classes.FilterComboboxMinMaxWidth
      : ''

    const isBondedLeftClass = placement === 'left'
      ? classes.isLeftBondedFilters
      : ''

    const isBondedRightClass = placement === 'right'
      ? classes.isRightBondedFilters
      : ''

    const isInputInvalidClass = isInvalid
      ? classes.Invalid
      : ''

    const comboBoxClasses = [
      classes.Combobox,
      enabledClass,
      hasControlledWidthClass,
      isBondedLeftClass,
      isBondedRightClass,
      isInputInvalidClass
    ].filter(className => className)

    return comboBoxClasses.join(' ')
  }

  const getComboBoxLeftPositionStyle = (comboBoxRef: React.RefObject<HTMLDivElement | null>): CSSProperties => {
    if (!comboBoxRef.current) {
      return { left: '0px' }
    }

    const comboxBoxPos = comboBoxRef.current.getBoundingClientRect().left + 410

    if (comboxBoxPos > windowWidth) {
      return { left: `${windowWidth - comboxBoxPos}px` }
    }

    return { left: '0px' }
  }

  const getComboBoxRightPositionStyle = (comboBoxRef: React.RefObject<HTMLDivElement | null>): CSSProperties => {
    if (!comboBoxRef.current) {
      return {
        top: '-2px',
        left: '115px'
      }
    }

    const comboxBoxPos = comboBoxRef.current.offsetWidth
    const comboBoxOffsetTop = comboBoxRef.current.offsetTop

    const topPosition = getTopPosition(
      comboBoxOffsetTop,
      groupedOptions.length > 0
        ? groupedOptions[0].options
        : options
    )

    return windowHeight < 1200
      ? {
        position: 'fixed',
        top: options.length === 0 && groupedOptions.length === 0
          ? '-2px'
          : topPosition,
        left: `${comboxBoxPos + 20}px`
      }
      : {
        top: '-2px',
        left: `${comboxBoxPos + 5}px`
      }
  }

  const handleInputClear = () => { setInput('') }

  return (
    <div ref={comboBoxRef}>
      <Downshift
        inputValue={input}
        itemToString={itemToString}
        key={value} // resolve AUB-19498 where parent call to change value downshift state is not updated
        onChange={handleSelection}
        onInputValueChange={onInputValueChange}
        onOuterClick={onOuterClick}
        stateReducer={stateReducer}
      >
        {
          ({
            getInputProps,
            getItemProps,
            getMenuProps,
            getRootProps,
            getToggleButtonProps,
            isOpen
          }) =>
            <div
              className={getComboBoxClasses()}
              {...getRootProps(undefined, { suppressRefError: true }) as Overwrite<GetRootPropsReturnValue, Options>}
              aria-disabled={isDisabled}
              role={undefined}
            >
              <input
                checked={!!value}
                onInvalid={(event: React.ChangeEvent<HTMLInputElement>) => event.target.setCustomValidity(customValidity)}
                readOnly
                ref={inputRef}
                required={isRequired}
                type='checkbox'
              />
              {renderButton(getToggleButtonProps())}
              {
                isOpen && !isDisabled &&
                  <div
                    className={classes.dropdownWrapper}
                    ref={divRef}
                    style={
                      dropdownPlacement === 'bottom'
                        ? getComboBoxLeftPositionStyle(comboBoxRef)
                        : getComboBoxRightPositionStyle(comboBoxRef)
                    }
                  >
                    {
                      options.length || groupedOptions.length
                        ? (
                          <>
                            {
                              hasSearch &&
                                <div className={classes.searchWrapper}>
                                  <SearchInput
                                    {
                                      ...getInputProps()
                                    }
                                    isAutoFocus
                                    onClear={handleInputClear}
                                  />
                                </div>
                            }
                            <ul
                              {...getMenuProps()}
                              onScroll={handleScroll}
                            >
                              {hasInitialOptions && renderInitialOptions(getItemProps)}
                              {hasInitialOptions && renderInitialGroupedOptions(getItemProps)}
                              {!!options.length && renderOptions(getItemProps)}
                              {!!groupedOptions.length && renderGroupedOptions(getItemProps)}
                              {defaultOptionCount === MAXIMUM_OPTIONS_COUNT && options.length > MAXIMUM_OPTIONS_COUNT && maximumListDescription(options.length)}
                              {defaultOptionCount === MAXIMUM_OPTIONS_COUNT && groupedOptions.length > MAXIMUM_OPTIONS_COUNT && maximumGroupListDescription(groupedOptions.length)}
                            </ul>
                            {hasClearButton && (!!externalQuery || value) && renderClearSection}
                          </>
                        )
                        : noOptionsPlaceholder
                    }
                    {footer && footer}
                  </div>
              }
            </div>
        }
      </Downshift>
      {
        isInvalid && (
          <div className={classes.ErrorMessage}>
            <FiAlertCircle
              aria-hidden
              className={classes.ErrorIcon}
              size='12px'
            />
            <span>{customValidity}</span>
          </div>
        )
      }
    </div>
  )
}

export default Combobox
